import { defineStore } from 'pinia'
import { markBoot, reportBoot } from '@/lib/bootprofile'
import { api, ApiError, setApiActiveOrg } from '@/lib/api'
import type { Me, Membership } from '@/lib/types'

/**
 * Cache keys are namespaced per Clerk user.
 *
 * They used to be global, which leaks org state between accounts signed in on
 * the same browser: sign in as one user, then another, and the second one's
 * first refresh sends the FIRST one's organization in X-Org-ID. The server
 * correctly answers "not a member of the requested organization", which reads
 * as being signed out — and signing out and back in appears to fix it because
 * it re-resolves the org, not because permissions changed.
 */
function clerkUserId(): string {
  return (window as any).Clerk?.user?.id ?? 'anon'
}

function lastOrgKey(): string {
  return `govcrm:lastOrgId:${clerkUserId()}`
}

function meCacheKey(): string {
  return `govcrm:me:${clerkUserId()}`
}

function readCachedMe(): Me | null {
  try {
    const raw = localStorage.getItem(meCacheKey())
    const me = raw ? (JSON.parse(raw) as Me) : null
    // Belt and braces: a cache that names a different user is not ours.
    if (me && clerkUserId() !== 'anon' && me.clerk_user_id !== clerkUserId()) return null
    return me
  } catch {
    return null
  }
}

/** Drop anything a previous account left behind on this browser. */
function clearStaleOrgState() {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k === 'govcrm:lastOrgId' || k === 'govcrm:me') localStorage.removeItem(k)
    }
  } catch {
    // best-effort
  }
}

export const useSessionStore = defineStore('session', {
  state: () => ({
    me: null as Me | null,
    activeOrgId: null as string | null,
    loading: false,
    error: null as ApiError | null,
    /** Admin-only UI preview: see the app as another role. Client-side
     * only — the server always enforces the REAL role. */
    viewAsRole: null as string | null,
  }),

  getters: {
    memberships(state): Membership[] {
      return state.me?.memberships ?? []
    },
    /** Role of the current user in the active org (from our DB, not Clerk). */
    /** The real role from the membership (server-enforced). */
    actualRole(state): string | null {
      return (
        state.me?.memberships.find((m) => m.org_id === state.activeOrgId)?.role ?? null
      )
    },
    /** What the UI gates on — the preview role when an admin is peeking. */
    role(state): string | null {
      const real =
        state.me?.memberships.find((m) => m.org_id === state.activeOrgId)?.role ?? null
      if (state.viewAsRole && (real === 'company_admin' || real === 'org_admin')) {
        return state.viewAsRole
      }
      return real
    },
    /**
     * Gmail drafting is gated on the member's OWN connection. Without it a
     * draft would land in the shared org mailbox — somewhere they can't see,
     * edit, or send it from — so every drafting affordance is disabled and
     * points at Settings instead.
     */
    canDraftEmail(state): boolean {
      return state.me?.gmail_connected === true
    },
    activeOrg(state): Membership | null {
      return (
        state.me?.memberships.find((m) => m.org_id === state.activeOrgId) ?? null
      )
    },
  },

  actions: {
    /**
     * Fetch /api/v1/me once and resolve the active org:
     * an org already set from the URL wins if it's a real membership, then a
     * single membership, then the last-used org from localStorage, then the
     * API's active org, then the first membership.
     */
    async init(): Promise<Me | null> {
      if (this.me) return this.me
      this.loading = true
      this.error = null

      // Instant paint: hydrate from the last confirmed /me while the real
      // request (which waits on ClerkJS + the network) runs. The fresh
      // response reconciles below; a 401 clears the cache.
      // Old global keys predate per-user namespacing and can point at
      // another account's org.
      clearStaleOrgState()

      const cached = readCachedMe()
      if (cached?.memberships?.length) {
        this.me = cached
        const last = localStorage.getItem(lastOrgKey())
        const isCachedMember = cached.memberships.some((m) => m.org_id === last)
        const orgId = isCachedMember ? last : cached.memberships[0]?.org_id
        if (orgId && !this.activeOrgId) this.setActiveOrg(orgId)
      }

      try {
        markBoot('meRequested')
        const me = await api.getMe()
        markBoot('meLoaded')
        reportBoot()
        this.me = me
        try {
          localStorage.setItem(meCacheKey(), JSON.stringify(me))
        } catch {
          // cache is best-effort
        }

        const isMember = (id: string | null): id is string =>
          !!id && me.memberships.some((m) => m.org_id === id)

        let orgId: string | null = this.activeOrgId
        if (!isMember(orgId)) {
          if (me.memberships.length === 1) {
            orgId = me.memberships[0].org_id
          } else {
            const last = localStorage.getItem(lastOrgKey())
            if (isMember(last)) {
              orgId = last
            } else if (isMember(me.active_org_id)) {
              orgId = me.active_org_id
            } else {
              orgId = me.memberships[0]?.org_id ?? null
            }
          }
        }
        if (orgId) this.setActiveOrg(orgId)
      } catch (e) {
        this.error = e instanceof ApiError ? e : new ApiError(0, (e as Error).message)
        if (e instanceof ApiError && e.status === 401) {
          // Signed out for real — drop the optimistic cache and state.
          localStorage.removeItem(meCacheKey())
          this.me = null
        }
      } finally {
        this.loading = false
      }
      return this.me
    },

    /** Set the active org — the API client sends it as X-Org-ID on every request. */
    setViewAsRole(role: string | null) {
      this.viewAsRole = role
    },

    setActiveOrg(orgId: string) {
      this.activeOrgId = orgId
      setApiActiveOrg(orgId)
      localStorage.setItem(lastOrgKey(), orgId)
    },
  },
})
